import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import 'swiper/css/a11y'
import 'swiper/css/grid'

import clsx from 'clsx'
import React, {
  type FC,
  type ForwardedRef,
  forwardRef,
  type PropsWithChildren,
  useEffect,
  useRef,
} from 'react'
import useWindowSize from 'react-use/lib/useWindowSize'
import { A11y, Grid, Keyboard, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

type SlideProps = {
  ref?: ForwardedRef<HTMLDivElement>
  className?: string
}

const Slide: FC<PropsWithChildren<SlideProps>> = forwardRef((props, ref) => (
  <SwiperSlide ref={ref} {...props}>
    {props.children}
  </SwiperSlide>
))

type CarouselProps = {
  rows: number
}

const Carousel: FC<PropsWithChildren<CarouselProps>> & {
  Slide: FC<PropsWithChildren<SlideProps>>
} = ({ rows, children }) => {
  const prevWidthRef = useRef<number>(0)
  const swiperRef = useRef(null)
  const { width } = useWindowSize()

  useEffect(() => {
    if (swiperRef.current) {
      if (prevWidthRef.current >= 768 && width < 768) {
        // Width changed from more than 768 to less than 768
        swiperRef.current.slideTo(1)
      }
    }

    prevWidthRef.current = width
  }, [width])

  return (
    <div
      className={clsx(
        'relative mb-10 transition-opacity duration-500'
        // isClient ? 'opacity-100' : 'opacity-0'
      )}
    >
      <Swiper
        centeredSlides
        grabCursor
        className="!px-14"
        initialSlide={1}
        modules={[Navigation, A11y, Keyboard, Grid]}
        slidesPerView={1}
        spaceBetween={0}
        style={{ overflow: 'clip' }}
        breakpoints={{
          768: {
            centeredSlides: false,
            grabCursor: false,
            slidesPerView: 3,
            spaceBetween: 0,
            initialSlide: 0,
            grid: {
              rows,
              fill: 'row',
            },
          },
        }}
        grid={{
          rows,
          fill: 'row',
        }}
        keyboard={{
          enabled: true,
        }}
        onSwiper={(swiper) => (swiperRef.current = swiper)}
      >
        {children}
      </Swiper>
    </div>
  )
}

// https://github.com/nolimits4web/swiper/issues/4413
Slide.displayName = 'SwiperSlide'

Carousel.Slide = Slide

export default Carousel
